/*
  section-product-frame — the option flip.

  Scope is deliberately tiny: the rail itself is CSS scroll-snap, and its
  arrows are driven by the shared `modulo-section-ledge-rail.js`. All this file
  does is upgrade one link into a disclosure.

  PROGRESSIVE ENHANCEMENT IS THE WHOLE DESIGN. Liquid renders the buy control
  for a multi-size product as a plain <a> to the product page, and the chip row
  is display:none, so with this script absent (or failed, or still loading) the
  card is a working link — never a control that does nothing. The ARIA that
  describes a disclosure is added HERE, on bind, so the markup never claims a
  state it does not have.
*/
(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var OPEN_SEL = '[data-pfr-open]';
  var QV_SEL = '[data-pfr-qv]';
  var FOCUSABLE = 'a[href],button:not([disabled]),select:not([disabled]),input:not([disabled]):not([type="hidden"]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var qvReturn = null;

  function sectionOf(el) { return el && el.closest ? el.closest('[data-modulo-pfr]') : null; }

  function modalFor(el) {
    var sec = sectionOf(el);
    var sid = sec ? sec.getAttribute('data-pfr-sid') : null;
    if (!sid) return null;
    return document.querySelector('[data-pfr-qvmodal][data-pfr-sid="' + sid + '"]');
  }

  function openModal() { return document.querySelector('[data-pfr-qvmodal]:not([hidden])'); }

  function qvOpen(trigger) {
    var wrap = modalFor(trigger);
    var tpl = document.getElementById(trigger.getAttribute('data-pfr-qv'));
    /* No payload means no modal. Falling through to the link is the correct
       failure: the <a> still points at the product page. */
    if (!wrap || !tpl || !tpl.content) return false;

    var body = wrap.querySelector('[data-pfr-qvbody]');
    var dialog = wrap.querySelector('[data-pfr-qvdialog]');
    if (!body || !dialog) return false;

    /* The payload is cloned from a <template>, never from the card. Nothing in
       the rail is ever duplicated, so no selector can resolve a second copy of
       a card and no handler is ever rebound to a clone — the wrong-product bug
       is unreachable here rather than merely guarded (the 2026-08-15 rule). */
    body.innerHTML = '';
    body.appendChild(tpl.content.cloneNode(true));

    var title = body.querySelector('[data-pfr-qvtitle]');
    if (title) dialog.setAttribute('aria-label', title.textContent);

    wrap.hidden = false;
    document.documentElement.classList.add('modulo-pfr-qvlock');
    qvReturn = trigger;
    dialog.scrollTop = 0;
    if (typeof dialog.focus === 'function') dialog.focus();
    return true;
  }

  function qvClose() {
    var wrap = openModal();
    if (!wrap) return;
    wrap.hidden = true;
    /* Emptied on close, so the cloned <form> stops existing. A hidden duplicate
       product form left in the document is a second thing a theme's own cart
       code can find. */
    var body = wrap.querySelector('[data-pfr-qvbody]');
    if (body) body.innerHTML = '';
    document.documentElement.classList.remove('modulo-pfr-qvlock');
    if (qvReturn && typeof qvReturn.focus === 'function') qvReturn.focus();
    qvReturn = null;
  }

  function trapTab(e, wrap) {
    var nodes = wrap.querySelectorAll(FOCUSABLE);
    var list = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].offsetParent !== null || nodes[i] === document.activeElement) list.push(nodes[i]);
    }
    if (!list.length) { e.preventDefault(); return; }
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && (document.activeElement === first || !wrap.contains(document.activeElement))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }


  function slotOf(el) { return el.closest ? el.closest('.modulo-pfr__slot') : null; }
  function mediaOf(slot) { return slot && slot.closest ? slot.closest('.modulo-pfr__media') : null; }

  /* The card's price follows the value under the pointer or the keyboard, so
     the figure on the card is the figure the chip will charge. Liquid
     pre-rendered every one of these strings: JS NEVER formats money (a theme's
     own formatMoney is not ours to call, and Ella's throws on a one-arg call —
     the `psh` rule). */
  function priceNode(slot) {
    var card = slot && slot.closest ? slot.closest('.modulo-pfr__card') : null;
    return card ? card.querySelector('[data-pfr-now]') : null;
  }
  function showPrice(slot, money) {
    var n = priceNode(slot);
    if (!n) return;
    /* Restore has to put back the NODES, not a string: a varying price renders
       a "From" span inside this element, and rewriting textContent would drop
       it — re-introducing the exact misrepresentation this exists to prevent.
       The original is cloned once, on the first change. */
    if (!n.__pfrOrig) n.__pfrOrig = n.cloneNode(true);
    if (money) {
      n.textContent = money;
      return;
    }
    var copy = n.__pfrOrig.cloneNode(true);
    while (n.firstChild) n.removeChild(n.firstChild);
    while (copy.firstChild) n.appendChild(copy.firstChild);
  }

  function close(slot) {
    if (!slot || !slot.classList.contains('is-open')) return;
    slot.classList.remove('is-open');
    var media = mediaOf(slot);
    if (media) media.classList.remove('is-open');
    var opener = slot.querySelector(OPEN_SEL);
    if (opener) opener.setAttribute('aria-expanded', 'false');
    showPrice(slot, null);
  }

  function closeAll(except) {
    var open = document.querySelectorAll('.modulo-pfr__slot.is-open');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== except) close(open[i]);
    }
  }

  function open(slot) {
    if (!slot) return;
    closeAll(slot);
    slot.classList.add('is-open');
    var media = mediaOf(slot);
    if (media) media.classList.add('is-open');
    var opener = slot.querySelector(OPEN_SEL);
    if (opener) opener.setAttribute('aria-expanded', 'true');
    /* Moving focus into the row is what makes this a disclosure rather than a
       decoration — a keyboard user who opened it lands on the first choice. */
    var first = slot.querySelector('.modulo-pfr__opt:not([disabled])');
    if (first && typeof first.focus === 'function') first.focus();
  }

  function init(root) {
    if (!root || root.hasAttribute('data-pfr-ready')) return;
    root.setAttribute('data-pfr-ready', '');

    /* The dialog is moved to <body>. `position: fixed` is resolved against the
       nearest ancestor with a transform, filter or containment, and a merchant
       theme can put one anywhere above this section — the scrim would then stop
       covering the viewport (the `hpi` warning, the `bte` fix).

       The section's custom properties live in its inline style attribute, which
       stops reaching the dialog the moment it leaves the subtree, so the
       attribute is copied across. Every dialog rule is scoped under
       `__qvwrap` rather than `.modulo-pfr` for the same reason. */
    var qv = root.querySelector('[data-pfr-qvmodal]');
    if (qv) {
      var sid = qv.getAttribute('data-pfr-sid');
      /* A theme-editor re-render emits a fresh dialog for the same section.
         Drop the previous one or they stack up invisibly. */
      var stale = document.querySelectorAll('body > [data-pfr-qvmodal][data-pfr-sid="' + sid + '"]');
      for (var s = 0; s < stale.length; s++) {
        if (stale[s] !== qv && stale[s].parentNode) stale[s].parentNode.removeChild(stale[s]);
      }
      var tokens = root.getAttribute('style');
      if (tokens) qv.setAttribute('style', tokens);
      document.body.appendChild(qv);
    }

    var qvs = root.querySelectorAll(QV_SEL);
    for (var q = 0; q < qvs.length; q++) {
      /* Stays an <a> with a real href, so with this script absent the eye goes
         to the product page instead of doing nothing. The only claim added here
         is the one that becomes true on bind. */
      qvs[q].setAttribute('aria-haspopup', 'dialog');
    }

    var openers = root.querySelectorAll(OPEN_SEL);
    for (var i = 0; i < openers.length; i++) {
      var a = openers[i];
      /* role=button, not the bare link role: with JS present this control does
         not navigate, and announcing it as a link would be a lie. Space is
         wired below because an <a> does not act on it natively. */
      a.setAttribute('role', 'button');
      a.setAttribute('aria-expanded', 'false');
      var target = a.getAttribute('data-pfr-target');
      if (target) a.setAttribute('aria-controls', target);
    }
  }

  function boot(scope) {
    var els = (scope || document).querySelectorAll('[data-modulo-pfr]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  /* An identical <script src> still executes once per tag, and two of these
     sections on one page emit two of them — guard the document listeners or
     every handler is bound twice (the `as`/`hpi` lesson). */
  if (!window.__moduloPfrWired) {
    window.__moduloPfrWired = true;

    document.addEventListener('click', function (e) {
      var opener = e.target.closest ? e.target.closest(OPEN_SEL) : null;
      if (opener) {
        e.preventDefault();
        var slot = slotOf(opener);
        if (slot && slot.classList.contains('is-open')) close(slot);
        else open(slot);
        return;
      }

      var qvTrigger = e.target.closest ? e.target.closest(QV_SEL) : null;
      if (qvTrigger) {
        /* Only swallow the navigation if the dialog actually opened. */
        if (qvOpen(qvTrigger)) e.preventDefault();
        return;
      }

      if (e.target.closest && e.target.closest('[data-pfr-qvclose]')) {
        e.preventDefault();
        qvClose();
        return;
      }

      var step = e.target.closest ? e.target.closest('[data-pfr-qty]') : null;
      if (step) {
        var qi = step.parentNode ? step.parentNode.querySelector('[data-pfr-qtyi]') : null;
        if (qi) {
          var n = parseInt(qi.value, 10);
          if (isNaN(n) || n < 1) n = 1;
          n += parseInt(step.getAttribute('data-pfr-qty'), 10) || 0;
          if (n < 1) n = 1;
          qi.value = String(n);
        }
        return;
      }

      /* A click inside the dialog is never a card dismissal. */
      if (e.target.closest && e.target.closest('[data-pfr-qvmodal]')) return;

      /* THE SUBMITTER'S name/value IS NOT IN `new FormData(form)`. Dawn and
         nearly every theme intercept the product form and build FormData
         without passing the submitter, which drops a submit button's own
         name/value — so an intercepted add would post no variant at all. The
         hidden input carries the same id for that path. It ships EMPTY: an
         empty id fails loudly, a defaulted one quietly charges the wrong
         variant. This runs on click, which precedes form submission. */
      var chip = e.target.closest ? e.target.closest('.modulo-pfr__opt[name="id"]') : null;
      if (chip && !chip.disabled) {
        var form = chip.closest('form');
        var hidden = form ? form.querySelector('[data-pfr-id]') : null;
        if (hidden) hidden.value = chip.value;
        return;
      }

      var closer = e.target.closest ? e.target.closest('[data-pfr-close]') : null;
      if (closer) {
        var s = slotOf(closer);
        close(s);
        var back = s ? s.querySelector(OPEN_SEL) : null;
        if (back && typeof back.focus === 'function') back.focus();
        return;
      }

      /* A click anywhere else dismisses. If anything was open, swallow that
         click: the photo link covers the whole frame, so the obvious
         tap-to-dismiss target would otherwise navigate as it closes — and the
         navigation wins. CSS drops the link's pointer-events while open too;
         this is the belt to that braces. */
      if (!(e.target.closest && e.target.closest('.modulo-pfr__opts'))) {
        var wasOpen = !!document.querySelector('.modulo-pfr__slot.is-open');
        var insideCard = !!(e.target.closest && e.target.closest('.modulo-pfr__card'));
        closeAll(null);
        if (wasOpen && insideCard) e.preventDefault();
      }
    });

    /* One <select> of whole variants, so a choice maps to exactly one variant
       however many options the product has. Every price string was rendered by
       Liquid into `data-price`; this only swaps strings and never formats
       money (the `psh` rule). */
    document.addEventListener('change', function (e) {
      var sel = e.target && e.target.closest ? e.target.closest('[data-pfr-qvselect]') : null;
      if (!sel) return;
      var form = sel.closest('form');
      if (!form) return;
      var hidden = form.querySelector('[data-pfr-qvid]');
      if (hidden) hidden.value = sel.value;
      var opt = sel.options[sel.selectedIndex];
      var now = form.parentNode ? form.parentNode.querySelector('[data-pfr-qvnow]') : null;
      if (opt && now && opt.getAttribute('data-price')) now.textContent = opt.getAttribute('data-price');
    });

    document.addEventListener('keydown', function (e) {
      var live = openModal();
      if (live) {
        if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); qvClose(); return; }
        if (e.key === 'Tab') { trapTab(e, live); return; }
      }
      if (e.key === 'Escape' || e.key === 'Esc') {
        var openSlot = document.querySelector('.modulo-pfr__slot.is-open');
        if (!openSlot) return;
        var back = openSlot.querySelector(OPEN_SEL);
        close(openSlot);
        if (back && typeof back.focus === 'function') back.focus();
        return;
      }
      if (e.key === ' ' || e.key === 'Spacebar') {
        var t = e.target;
        if (t && t.closest && t.closest(OPEN_SEL)) {
          e.preventDefault();
          t.closest(OPEN_SEL).click();
        }
      }
    });

    document.addEventListener('pointerover', function (e) {
      var chip = e.target.closest ? e.target.closest('.modulo-pfr__opt[data-pfr-price]') : null;
      if (chip) showPrice(slotOf(chip), chip.getAttribute('data-pfr-price'));
    });
    document.addEventListener('focusin', function (e) {
      var chip = e.target.closest ? e.target.closest('.modulo-pfr__opt[data-pfr-price]') : null;
      if (chip) showPrice(slotOf(chip), chip.getAttribute('data-pfr-price'));
    });

    /* Leaving a card with the keyboard closes what that card opened, so a
       tab-through never trails open panels behind it. */
    document.addEventListener('focusout', function (e) {
      var slot = slotOf(e.target);
      if (!slot || !slot.classList.contains('is-open')) return;
      window.setTimeout(function () {
        if (!slot.contains(document.activeElement)) close(slot);
      }, 0);
    });

    document.addEventListener('shopify:section:load', function (e) {
      boot(e.target || document);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { boot(document); });
    }
  }

  if (document.readyState !== 'loading') boot(document);
})();
